import React from 'react';

const CaseStudies = () => {
  return (
    <section id="case-studies" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Outcome Banner */}
        <div className="text-center mb-16">
          <div className="max-w-4xl mx-auto bg-primary/5 border-l-4 border-primary p-8 rounded-lg">
            <p className="text-xl font-semibold text-gray-900">
              We don't build generic software. We redesign business workflows and implement systems that remove operational inefficiencies.
            </p>
          </div>
        </div>

        {/* Metrics Section */}
        <div className="grid md:grid-cols-5 gap-6 max-w-6xl mx-auto mb-16">
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <div className="text-3xl font-bold text-primary mb-2">1</div>
            <p className="text-gray-700 font-medium">Manufacturing ERP Transformation</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <div className="text-3xl font-bold text-primary mb-2">20+</div>
            <p className="text-gray-700 font-medium">Operational Workflows Digitized</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <div className="text-3xl font-bold text-primary mb-2">Real-Time</div>
            <p className="text-gray-700 font-medium">Inventory & Procurement Automation</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <div className="text-3xl font-bold text-primary mb-2">AI-Driven</div>
            <p className="text-gray-700 font-medium">Purchase Suggestion Layer</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <div className="text-3xl font-bold text-primary mb-2">₹5K/Month</div>
            <p className="text-gray-700 font-medium">Lightweight Maintenance Model</p>
          </div>
        </div>

        {/* Main Case Study: Manufacturing ERP */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-secondary text-white px-8 py-6">
              <h2 className="text-2xl font-bold mb-2">Manufacturing ERP Transformation – Refractories Company</h2>
              <p className="text-white/90">Complete operational workflow redesign for manufacturing excellence</p>
            </div>
            
            <div className="p-8">
              <div className="grid md:grid-cols-3 gap-8">
                {/* The Real Business Problem */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="ri-error-warning-line text-red-600"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">The Real Business Problem</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 italic">The factory was already using an ERP system. However:</p>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Supervisors recorded stock usage and production data on paper</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      <span>One operator manually re-entered data into ERP</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Complex desktop UI discouraged real-time entries</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Data was exported to Excel and Tally for analysis</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Indent creation and purchase tracking required manual coordination</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-close-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                      <span>No real-time visibility into stock consumption or shortages</span>
                    </li>
                  </ul>
                  <p className="text-sm font-semibold text-red-700 mt-3">The ERP existed — but ground-level execution and system adoption were misaligned.</p>
                </div>

                {/* What We Implemented */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-tools-line text-blue-600"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">What We Implemented</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Designed a simplified, workflow-aligned system for real-time stock and production entries</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Enabled direct supervisor-level data entry (eliminating paper dependency)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Real-time stock level and stock usage tracking</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Automated indent creation based on dynamic stock thresholds</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Auto-filled purchase and MRN workflows</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Integrated inventory, procurement, and usage tracking</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Built dynamic reporting engine with pivot-style analytics</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Implemented AI-powered purchase suggestions</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Delivered the system with lightweight maintenance cost (₹5K/month)</span>
                    </li>
                  </ul>
                </div>

                {/* Business Impact */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-line-chart-line text-green-600"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Business Impact</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-up-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Eliminated duplicate paper-to-ERP data flow</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-up-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Improved ERP adoption at supervisor level</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-up-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Enabled real-time stock and production visibility</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-up-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Automated indent and procurement workflows</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-up-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Reduced Excel dependency for management reporting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-up-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>Increased operational transparency without replacing existing ERP</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-700 mb-4">
            Ready to transform your operations?
          </p>
          <a 
            href="#contact" 
            className="inline-block px-8 py-3 bg-primary text-white font-medium rounded-button hover:bg-primary/90 transition-all transform hover:-translate-y-1 shadow-lg"
          >
            Let's Discuss Your Project
          </a>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
